import * as React from "react";
import KuikaButton, { type IKuikaButtonStatusLabels, type KuikaButtonStatus } from "../kuika-button/kuika-button";
import "./k-modal-footer.scss";

/**
 * The row of decisions at the bottom of a dialog.
 *
 * The designer has ~147 dialogs and had five different footers: three button sizes plus a
 * large population that silently took the default, ALL CAPS in about half the labels and
 * Title Case in the other half, the destructive action on the left in the Config Manager
 * and on the right everywhere else, and five different treatments of that action. This is
 * the one footer, and it decides all of that in one place:
 *
 *  - one size (`md`), because a dialog's buttons are controls, not headlines;
 *  - one casing, applied by `uppercase` rather than typed into the label, so a caller can
 *    write "Delete" and get what every other dialog gets;
 *  - the destructive action on the LEFT, at the other end from the commit — an irreversible
 *    decision should never sit under the pointer on its way to the safe one.
 *
 * It goes in `KModal`'s own `footer` slot, so it stays put while the body scrolls. That
 * puts it outside any `<form>` in the body, which is why `submitForm` exists: the HTML
 * `form` attribute associates the button with a form anywhere in the document.
 */

export interface IKModalFooterProps {
  /**
   * The irreversible one — Delete, Remove, Revert. Rendered on the left, and only when the
   * caller has something to destroy: a create dialog passes nothing.
   */
  onDelete?: () => void;
  deleteLabel?: string;
  deleteTestId?: string;
  /** For the onboarding layer, which addresses steps by `data-onboarding-id`. */
  deleteOnboardingId?: string;
  /** The way out. Most dialogs have one; a few (a single-action confirm) do not. */
  onCancel?: () => void;
  cancelLabel?: string;
  cancelTestId?: string;
  /** For the onboarding layer, which addresses steps by `data-onboarding-id`. */
  cancelOnboardingId?: string;
  /** The commit. Every footer has one — a footer with nothing to commit is not a footer. */
  submitLabel: string;
  submitTestId?: string;
  /** For the onboarding layer, which addresses steps by `data-onboarding-id`. */
  submitOnboardingId?: string;
  /** Omit when the button submits a form rather than running a handler. */
  onSubmit?: () => void;
  /**
   * The `id` of the `<form>` this commits, for a dialog whose fields are an antd `Form`.
   * The footer is a sibling of the body, never a descendant, so nesting cannot do it.
   */
  submitForm?: string;
  /** A commit that is itself destructive — "Delete branch", "Deactivate". */
  submitTone?: "default" | "danger";
  /**
   * The commit is running. Only the commit spins; Delete and Cancel stand down with it, because
   * they are not what is running — a spinning Delete during a save read as "deleting".
   */
  loading?: boolean;
  /**
   * The stateful commit (KuikaButton `status`): the commit rolls to "Saving", then "Saved" or "Try
   * again", and its width follows. Delete and Cancel stand down while it is `loading`, as with
   * `loading`. When given, `loading` is ignored for the commit.
   */
  status?: KuikaButtonStatus;
  statusLabels?: IKuikaButtonStatusLabels;
  /**
   * Where the destructive action stands, on its own button: the control that was pressed is the
   * one that says what is happening. Delete does not normally reach `success` — it closes the
   * dialog — so its default labels stop at "Deleting". While it runs, the commit stands down.
   */
  deleteStatus?: KuikaButtonStatus;
  deleteStatusLabels?: IKuikaButtonStatusLabels;
  /**
   * The dialog closes itself once the commit's ✓ has been seen. The way out then stands down
   * through the success hold rather than flickering back on for the moment before the dialog
   * goes. A dialog that STAYS open after a successful commit leaves this off — there Cancel is
   * a live way out again, which is the default.
   */
  closesOnSuccess?: boolean;
  disabled?: boolean;
  /** Extra controls beside the destructive one, for the dialogs that have one. */
  children?: React.ReactNode;
}

/** Delete says what it is doing on its own button; it closes rather than reaching a ✓. */
const DEFAULT_DELETE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Deleting", success: "Deleted" };

export const KModalFooter = ({
  onDelete,
  deleteLabel = "Delete",
  deleteTestId,
  deleteOnboardingId,
  onCancel,
  cancelLabel = "Cancel",
  cancelTestId,
  cancelOnboardingId,
  submitLabel,
  submitTestId,
  submitOnboardingId,
  onSubmit,
  submitForm,
  submitTone = "default",
  loading,
  status,
  statusLabels,
  deleteStatus,
  deleteStatusLabels = DEFAULT_DELETE_STATUS_LABELS,
  closesOnSuccess = false,
  disabled,
  children
}: IKModalFooterProps): JSX.Element => {
  const isCommitSettling: boolean = closesOnSuccess && status === "success";
  const isCommitRunning: boolean = status === undefined ? Boolean(loading) : status === "loading" || isCommitSettling;
  const isDeleteRunning: boolean = deleteStatus === "loading";

  return (
    <div className="k-modal-footer__row">
      <span className="k-modal-footer__start">
        {onDelete && (
          <KuikaButton
            data-testid={deleteTestId}
            data-onboarding-id={deleteOnboardingId}
            type="secondary"
            color="danger"
            size="md"
            uppercase
            disabled={isCommitRunning}
            status={deleteStatus}
            statusLabels={deleteStatusLabels}
            onClick={onDelete}
          >
            {deleteLabel}
          </KuikaButton>
        )}
        {children}
      </span>
      <span className="k-modal-footer__end">
        {onCancel && (
          <KuikaButton
            data-testid={cancelTestId}
            data-onboarding-id={cancelOnboardingId}
            type="tertiary"
            color="neutral"
            size="md"
            uppercase
            disabled={isCommitRunning || isDeleteRunning}
            onClick={onCancel}
          >
            {cancelLabel}
          </KuikaButton>
        )}
        <KuikaButton
          data-testid={submitTestId}
          data-onboarding-id={submitOnboardingId}
          type="primary"
          color={submitTone === "danger" ? "danger" : "default"}
          size="md"
          uppercase
          htmlType={onSubmit ? "button" : "submit"}
          form={submitForm}
          loading={loading}
          status={status}
          statusLabels={statusLabels}
          disabled={disabled || isDeleteRunning}
          onClick={onSubmit}
        >
          {submitLabel}
        </KuikaButton>
      </span>
    </div>
  );
};
